import { useRef, useState } from 'react';
import { Box, Text, Title, Stack, SimpleGrid, Loader, Alert, Button, Group, Tooltip, ActionIcon } from '@mantine/core';
import { Folder, FolderOpen, FileText, Upload, CheckCircle, AlertCircle, Library, FolderPlus, Pencil, Trash2, RefreshCw } from 'lucide-react';
import { CollectionResponse } from '../../models/responses/CollectionResponse';
import { FolderNode } from './folderUtils';
import ProcessingStatusBadge from '../common/ProcessingStatusBadge';
import DocumentPreviewModal from '../DocumentPreviewModal';

interface UploadStatus {
  type: 'success' | 'error';
  message: string;
}

interface FolderDetailsPanelProps {
  selectedFolder: FolderNode | null;
  allCollections: CollectionResponse[];
  onSelect: (node: FolderNode) => void;
  documents: any[];
  isLoadingDocuments: boolean;
  onUpload: (files: File[]) => void;
  isUploading: boolean;
  uploadStatus: UploadStatus | null;
  totalCount?: number;
  // Action handlers (previously in toolbar)
  onNewFolder: () => void;
  onRename: () => void;
  onDelete: () => void;
  onRefresh: () => void;
  onClickUpload: () => void;
  isOperating: boolean;
  // Single-document delete - triggered inline per row, no selection step needed
  onDeleteFile: (doc: any) => void;
  isSuperAdmin: boolean;
}

const ACCEPTED_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

function SubfolderCard({ node, onSelect }: { node: FolderNode; onSelect: (n: FolderNode) => void }) {
  return (
    <Box
      onClick={() => onSelect(node)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        padding: '12px 8px',
        borderRadius: 8,
        border: '1px solid #e8e0d4',
        background: '#faf8f3',
        cursor: 'pointer',
        transition: 'background 0.15s, border-color 0.15s',
        userSelect: 'none',
        minWidth: 80,
        maxWidth: 120,
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.background = '#f0ebe0';
        (e.currentTarget as HTMLElement).style.borderColor = '#c8b89a';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.background = '#faf8f3';
        (e.currentTarget as HTMLElement).style.borderColor = '#e8e0d4';
      }}
    >
      <Folder size={28} color="#8a6a4a" />
      <Text
        size="xs"
        fw={500}
        c="#3d3428"
        style={{
          textAlign: 'center',
          wordBreak: 'break-word',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
        title={node.name}
      >
        {node.name}
      </Text>
    </Box>
  );
}

function DocumentRow({
  doc,
  onPreview,
  onDelete,
  busy,
  canDelete,
}: {
  doc: any;
  onPreview: (doc: any) => void;
  onDelete: (doc: any) => void;
  busy: boolean;
  canDelete: boolean;
}) {
  const fileName = doc.fileName ?? doc.FileName ?? 'Untitled';
  const status = doc.status ?? doc.Status;
  const dateUploaded = doc.dateUploadedUtc ?? doc.DateUploadedUtc;

  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        borderRadius: 8,
        border: '1px solid #e8e0d4',
        background: '#fafafa',
      }}
    >
      <Tooltip label="Preview document">
        <ActionIcon
          variant="subtle"
          color="#8a6a4a"
          size="sm"
          style={{ flexShrink: 0 }}
          onClick={() => onPreview(doc)}
          aria-label={`Preview ${fileName}`}
        >
          <FileText size={16} />
        </ActionIcon>
      </Tooltip>
      <Box style={{ flex: 1, overflow: 'hidden' }}>
        <Text
          size="sm"
          c="#3d3428"
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={fileName}
        >
          {fileName}
        </Text>
        {dateUploaded && (
          <Text size="xs" c="dimmed">{new Date(dateUploaded).toLocaleDateString()}</Text>
        )}
      </Box>
      {status !== undefined && status !== null && (
        <ProcessingStatusBadge status={status} />
      )}
      {canDelete && (
        <Tooltip label="Delete file">
          <ActionIcon
            variant="subtle"
            color="red"
            size="sm"
            style={{ flexShrink: 0 }}
            onClick={() => onDelete(doc)}
            disabled={busy}
            aria-label={`Delete ${fileName}`}
          >
            <Trash2 size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Box>
  );
}

export default function FolderDetailsPanel({
  selectedFolder,
  allCollections,
  onSelect,
  documents,
  isLoadingDocuments,
  onUpload,
  isUploading,
  uploadStatus,
  totalCount,
  onNewFolder,
  onRename,
  onDelete,
  onRefresh,
  onClickUpload,
  isOperating,
  onDeleteFile,
  isSuperAdmin,
}: FolderDetailsPanelProps) {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  // Document opened in the preview modal (clicking a document's file icon).
  const [previewDocument, setPreviewDocument] = useState<any | null>(null);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (!isSuperAdmin) return;
    dragCounter.current++;
    if (dragCounter.current === 1) setIsDragging(true);
  };
  const handleDragLeave = () => {
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    if (!isSuperAdmin) return;
    const files = Array.from(e.dataTransfer.files).filter(f => ACCEPTED_MIME.includes(f.type));
    if (files.length > 0) onUpload(files);
  };

  if (!selectedFolder) {
    return (
      <Box
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px',
          textAlign: 'center',
        }}
      >
        <Library size={40} color="#c8c0b4" />
        <Text size="sm" c="dimmed" mt="md">Select a folder to view its contents.</Text>
      </Box>
    );
  }

  const col = selectedFolder.originalCollection;
  const isSystem = col.IsSystemCollection === true;
  const children = selectedFolder.children;
  const hasChildren = children.length > 0;
  const docCount = typeof totalCount === 'number' ? totalCount : documents.length;
  const hasDocuments = docCount > 0;
  const isEmpty = !hasChildren && !hasDocuments && !isLoadingDocuments;
  const busy = isOperating || isUploading;

  const parentName = !isSystem && col.ParentCollectionId
    ? allCollections.find(c => c.Id === col.ParentCollectionId)?.Name ?? null
    : null;

  const newFolderLabel = isSystem ? 'New Folder' : `New Folder in "${selectedFolder.name}"`;
  const FolderIcon = isSystem ? Library : FolderOpen;
  const folderIconColor = isSystem ? '#6a5acd' : '#8a6a4a';

  return (
    <Box
      style={{ height: '100%', position: 'relative' }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag-over overlay */}
      {isDragging && !isUploading && (
        <Box
          style={{
            position: 'absolute', inset: 0, zIndex: 10,
            background: 'rgba(138, 106, 74, 0.08)',
            border: '2px dashed #8a6a4a', borderRadius: 8,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 8,
            pointerEvents: 'none',
          }}
        >
          <Upload size={32} color="#8a6a4a" />
          <Text fw={600} c="#8a6a4a">Drop files to upload into "{selectedFolder.name}"</Text>
          <Text size="xs" c="dimmed">PDF, DOC, DOCX supported</Text>
        </Box>
      )}

      {/* Uploading overlay */}
      {isUploading && (
        <Box
          style={{
            position: 'absolute', inset: 0, zIndex: 10,
            background: 'rgba(255,255,255,0.85)', borderRadius: 8,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 12,
          }}
        >
          <Loader size="md" color="#8a6a4a" />
          <Text fw={500} c="#8a6a4a">Uploading into "{selectedFolder.name}"…</Text>
        </Box>
      )}

      <Box p="lg">
        {/* === Header: folder name + action buttons === */}
        <Box
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 12,
          }}
        >
          {/* Left: name + badges + meta */}
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FolderIcon size={20} color={folderIconColor} style={{ flexShrink: 0 }} />
              <Title order={4} fw={700} c="#2f271f" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedFolder.name}
              </Title>
            </Box>

            <Text size="xs" c="dimmed">
              {isSystem
                ? `${children.length} folder${children.length !== 1 ? 's' : ''} · ${isLoadingDocuments ? '…' : docCount} document${docCount !== 1 ? 's' : ''}`
                : `${children.length} subfolder${children.length !== 1 ? 's' : ''} · ${isLoadingDocuments ? '…' : docCount} document${docCount !== 1 ? 's' : ''}${parentName ? ` · in ${parentName}` : ''}`
              }
            </Text>
          </Stack>

          {/* Right: action buttons */}
          <Group gap={6} style={{ flexShrink: 0 }}>
            {isSuperAdmin && (
              <Tooltip label={newFolderLabel} withArrow>
                <Button
                  size="xs"
                  variant="light"
                  color="dark"
                  leftSection={<FolderPlus size={13} />}
                  onClick={onNewFolder}
                  disabled={busy}
                >
                  New Folder
                </Button>
              </Tooltip>
            )}

            {isSuperAdmin && (
              <Tooltip label="Upload files into this folder" withArrow>
                <Button
                  size="xs"
                  variant="light"
                  color="teal"
                  leftSection={<Upload size={13} />}
                  onClick={onClickUpload}
                  disabled={busy}
                  loading={isUploading}
                >
                  Upload
                </Button>
              </Tooltip>
            )}

            <Tooltip label={isSystem ? 'Cannot rename system collection' : 'Rename this folder'} withArrow>
              <Button
                size="xs"
                variant="light"
                color="dark"
                leftSection={<Pencil size={13} />}
                onClick={onRename}
                disabled={isSystem || busy}
              >
                Rename
              </Button>
            </Tooltip>

            {isSuperAdmin && (
              <Tooltip label={isSystem ? 'Cannot delete system collection' : 'Delete this folder'} withArrow>
                <Button
                  size="xs"
                  variant="light"
                  color="red"
                  leftSection={<Trash2 size={13} />}
                  onClick={onDelete}
                  disabled={isSystem || busy}
                >
                  Delete folder
                </Button>
              </Tooltip>
            )}

            <Tooltip label="Refresh" withArrow>
              <Button
                size="xs"
                variant="subtle"
                color="dark"
                leftSection={<RefreshCw size={13} />}
                onClick={onRefresh}
                disabled={busy}
              >
                Refresh
              </Button>
            </Tooltip>
          </Group>
        </Box>

        {/* Upload status */}
        {uploadStatus && (
          <Alert
            icon={uploadStatus.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            color={uploadStatus.type === 'success' ? 'teal' : 'red'}
            mb="md"
            withCloseButton={false}
          >
            {uploadStatus.message}
          </Alert>
        )}

        {/* === Contents === */}
        <Box style={{ borderTop: '1px solid #e8e0d4', paddingTop: 16 }}>
          <Text size="xs" fw={600} c="dimmed" mb="md" style={{ letterSpacing: '0.04em' }}>
            CONTENTS
          </Text>

          {isEmpty ? (
            <Box
              style={{
                background: '#f7f4ed',
                border: '1px dashed #c8c0b4',
                borderRadius: 8,
                padding: '40px 24px',
                textAlign: 'center',
              }}
            >
              <Upload size={28} color="#c8c0b4" style={{ marginBottom: 8 }} />
              <Text size="sm" c="dimmed">This folder is empty.</Text>
              <Text size="xs" c="dimmed" mt={4}>
                Create a subfolder or drag files here to upload.
              </Text>
            </Box>
          ) : (
            <Stack gap="lg">
              {hasChildren && (
                <Box>
                  <Text size="xs" c="dimmed" fw={500} mb="sm">
                    {isSystem ? `Root Folders (${children.length})` : `Folders (${children.length})`}
                  </Text>
                  <SimpleGrid cols={{ base: 3, sm: 5, md: 7, lg: 9 }} spacing="sm">
                    {children.map(child => (
                      <SubfolderCard key={child.id} node={child} onSelect={onSelect} />
                    ))}
                  </SimpleGrid>
                </Box>
              )}

              <Box>
                <Text size="xs" c="dimmed" fw={500} mb="sm">
                  Documents {isLoadingDocuments ? <Loader size={10} ml={4} /> : `(${docCount})`}
                </Text>
                {isLoadingDocuments ? (
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0' }}>
                    <Loader size="xs" />
                    <Text size="sm" c="dimmed">Loading documents…</Text>
                  </Box>
                ) : documents.length > 0 ? (
                  <Stack gap="xs">
                    {documents.map((doc: any, i: number) => {
                      const docId = doc.id ?? doc.Id ?? i;
                      return (
                        <DocumentRow
                          key={docId}
                          doc={doc}
                          onPreview={setPreviewDocument}
                          onDelete={onDeleteFile}
                          busy={busy}
                          canDelete={isSuperAdmin}
                        />
                      );
                    })}
                  </Stack>
                ) : docCount > 0 ? (
                  <Text size="sm" c="dimmed">There are {docCount} document{docCount !== 1 ? 's' : ''} in this folder, but none are loaded in this view.</Text>
                ) : (
                  <Text size="sm" c="dimmed">No documents in this folder.</Text>
                )}
              </Box>
            </Stack>
          )}
        </Box>
      </Box>

      <DocumentPreviewModal
        file={previewDocument}
        isOpen={previewDocument !== null}
        onClose={() => setPreviewDocument(null)}
      />
    </Box>
  );
}
